import { useTranslate } from '@va/localization';
import {
  fontWeights,
  Paragraph,
  paragraphSizes,
  SearchInputProps,
  SelectDropdownInputOption,
} from '@va/ui/design-system';
import { useTooltipContext } from '@va/ui/tooltips';
import { useFocusedActions } from '@va/util/hooks';
import { useAriaListboxPattern, useIsolatedFocusedContainer } from '@va/util/misc';
import classNames from 'classnames';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { SearchField } from '../search-field';
import { RadioInputSection } from './RadioInputSection';

type RadioInputSectionListProps = {
  options: SelectDropdownInputOption[];
  selectedOption?: SelectDropdownInputOption;
  onChange: (option: SelectDropdownInputOption) => void;
  searchInputProps?: SearchInputProps;
  isLoading?: boolean;
  id: string;
  noSelectionErrorMessage?: string;
  hint?: string;
  floating?: boolean;
};

export const RadioInputSectionList: React.FC<RadioInputSectionListProps> = ({
  id,
  options,
  selectedOption,
  onChange,
  searchInputProps,
  isLoading,
  noSelectionErrorMessage,
  floating,
  hint,
}) => {
  const translate = useTranslate();
  const tooltip = useTooltipContext();
  const [searchValue, setSearchValue] = useState('');
  const [filteredOptions, setFilteredOptions] = useState<SelectDropdownInputOption[]>(options);
  const [showEnterError, setShowEnterError] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { listboxProps } = useAriaListboxPattern<HTMLDivElement>({ id });
  useIsolatedFocusedContainer({ containerRef, enabled: tooltip.context?.open });

  const handleEnterWithNoSelection = useCallback(() => {
    if (!selectedOption && noSelectionErrorMessage) {
      setShowEnterError(true);
    }
  }, [selectedOption, noSelectionErrorMessage]);

  useFocusedActions({ ref: searchInputRef, triggerKeys: ['Enter'], onAction: handleEnterWithNoSelection });

  const defaultOnSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  useEffect(() => {
    if (!searchValue) {
      setFilteredOptions(options);
      return;
    }
    setFilteredOptions(
      options.filter(
        (option) =>
          option.value.toLowerCase().includes(searchValue.toLowerCase()) ||
          option.label.toLowerCase().includes(searchValue.toLowerCase()),
      ),
    );
  }, [options, searchValue]);

  const showSearchField = searchInputProps?.isVisible ?? true;

  return (
    <div
      ref={containerRef}
      className={classNames('text-gray-charcoal w-full bg-white rounded-12', { 'absolute z-999 shadow-lg': floating })}
    >
      {showSearchField && (
        <div className='px-3 pt-3 mb-2'>
          <SearchField
            className=''
            inputRef={searchInputRef}
            clearField={
              searchInputProps?.customClearField ? searchInputProps.customClearField : () => setSearchValue('')
            }
            placeholder={searchInputProps?.placeholder}
            value={searchInputProps?.searchValue ? searchInputProps.searchValue : searchValue}
            onChange={searchInputProps?.customOnSearch ? searchInputProps.customOnSearch : defaultOnSearch}
            data-testid={searchInputProps?.['data-testid']}
          />
        </div>
      )}
      {showEnterError && noSelectionErrorMessage && (
        <Paragraph
          colorClassName='text-red-negative'
          className='px-3 pt-1'
          size={paragraphSizes.tiny}
          weight={fontWeights.medium}
        >
          {noSelectionErrorMessage}
        </Paragraph>
      )}
      {/* A note about the options reads as nonsense when there are none to describe. */}
      {hint && !!filteredOptions.length && (
        <Paragraph
          colorClassName='text-gray-charcoal'
          className='px-3 pt-1'
          size={paragraphSizes.tiny}
          weight={fontWeights.medium}
        >
          {hint}
        </Paragraph>
      )}
      <div className='max-h-64 p-3 overflow-auto scrollbar scrollbar-thumb' {...listboxProps}>
        {filteredOptions.length === 0 && !isLoading && (
          <Paragraph className='p-5'>{translate('select.noOptions')}</Paragraph>
        )}
        {filteredOptions?.map(
          (
            {
              icon,
              label,
              value,
              smallLabel,
              ref,
              disabled,
              infoTooltip,
              labelTooltip,
              info,
              infoProps,
              endIcon,
              className,
              selectedColor,
              'data-testid': dataTestId,
            },
            index,
          ) => {
            return (
              <RadioInputSection
                id={`${id}-${index}`}
                infoTooltip={infoTooltip}
                titleTooltip={labelTooltip}
                disabled={disabled}
                elementRef={ref}
                key={index}
                className={classNames(
                  'group bg-white hover:bg-white-snow active:bg-gray-concrete',
                  className as string,
                )}
                icon={icon}
                selected={value === selectedOption?.value}
                title={label}
                subtitle={smallLabel}
                value={value}
                info={info}
                infoProps={infoProps}
                endIcon={endIcon}
                data-testid={dataTestId}
                onClick={(value) => {
                  const option = options.find((option) => option.value === value);
                  if (!option) return;
                  setShowEnterError(false);
                  onChange(option);
                }}
                selectedColor={selectedColor}
              />
            );
          },
        )}
        {isLoading && <Paragraph className='text-center py-2'>{translate('label.loading')}...</Paragraph>}
      </div>
    </div>
  );
};
